import { useEffect, useRef, useState } from 'react';
import { Loader2, Mic, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import {
  formatElapsed,
  releaseMicrophoneStream,
  requestMicrophoneStream,
  startVoiceRecording,
  type VoiceRecorderHandle,
  type VoiceRecordingResult,
} from '@/lib/voice';
import { createVoiceNote, requestVoiceNoteUpload } from '@/lib/api/capture';
import { uploadAssetBytes } from '@/lib/api/client';
import { enqueueVoiceNote } from '@/lib/offline';
import type { VoiceNotePurpose } from '@/lib/api/types';

type RecordingState = 'idle' | 'requesting' | 'recording' | 'uploading' | 'queued' | 'uploaded' | 'error';

export interface VoiceNoteRecorded {
  purpose: VoiceNotePurpose;
  durationMs: number;
  /** True when the clip could not reach the server on the spot and is
   *  waiting in the offline queue instead. */
  queued: boolean;
}

export interface VoiceNoteButtonProps {
  purpose: VoiceNotePurpose;
  /** The capture this note belongs to, once it has a server id. Set from
   *  the review screen's refine panel. */
  captureId?: string;
  /** The `queue` row this note travels with, before that capture has a
   *  server id. Set from the viewfinder's mic. */
  queuedCaptureLocalId?: string;
  onRecorded?: (result: VoiceNoteRecorded) => void;
  className?: string;
}

const LABEL: Record<VoiceNotePurpose, string> = {
  NOTE: 'Record a voice note',
  CONTEXT: 'Record a hint for Rerun AI',
};

const STATUS_LINE: Partial<Record<RecordingState, string>> = {
  requesting: 'Starting the microphone…',
  uploading: 'Saving the voice note…',
  queued: 'Saved. Uploads once you have a connection.',
  uploaded: 'Voice note saved.',
};

/**
 * The mic control on the viewfinder (purpose `NOTE`) and, exported here,
 * on the review screen's refine panel (purpose `CONTEXT`), per
 * INTERFACES.md 15.3 (W1).
 *
 * Records with `MediaRecorder` (`lib/voice/recorder.ts`), which stops the
 * clip itself at `MAX_VOICE_NOTE_DURATION_MS`. A clip against a capture
 * that already has a server id uploads straight away when there is a
 * connection; every other case, offline or no `captureId` yet, hands the
 * bytes to the offline queue (`lib/offline/voice-queue.ts`) so the clip
 * waits with its capture and uploads on reconnect rather than being lost
 * because the operator was mid-shift with no signal.
 *
 * The caller supplies exactly one of `captureId` or
 * `queuedCaptureLocalId`, the two ways a note finds its way back to a
 * capture (`db.ts` `QueuedVoiceNote`).
 */
export function VoiceNoteButton({
  purpose,
  captureId,
  queuedCaptureLocalId,
  onRecorded,
  className,
}: VoiceNoteButtonProps) {
  const online = useOnlineStatus();
  const [state, setState] = useState<RecordingState>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const handleRef = useRef<VoiceRecorderHandle | null>(null);

  const attached = Boolean(captureId || queuedCaptureLocalId);

  // A live mic stream and an in-flight recorder are both real browser
  // resources, so leaving this screen mid-recording releases both rather
  // than leaving the browser's recording indicator lit for nothing.
  useEffect(() => {
    return () => {
      handleRef.current?.cancel();
      if (streamRef.current) releaseMicrophoneStream(streamRef.current);
    };
  }, []);

  async function finish(result: VoiceRecordingResult) {
    if (streamRef.current) {
      releaseMicrophoneStream(streamRef.current);
      streamRef.current = null;
    }
    handleRef.current = null;

    if (!attached) {
      setState('idle');
      return;
    }

    if (online && captureId) {
      setState('uploading');
      try {
        const uploadUrlResponse = await requestVoiceNoteUpload(captureId, {
          contentType: result.contentType,
          byteLength: result.blob.size,
          durationMs: result.durationMs,
          purpose,
        });
        await uploadAssetBytes(uploadUrlResponse.uploadUrl, result.blob, result.contentType);
        await createVoiceNote(captureId, {
          noteId: uploadUrlResponse.noteId,
          audioKey: uploadUrlResponse.audioKey,
          durationMs: result.durationMs,
          purpose,
        });
        setState('uploaded');
        onRecorded?.({ purpose, durationMs: result.durationMs, queued: false });
        return;
      } catch {
        // Falls through to the offline queue: the operator already spoke
        // the clip once, and a refused upload should not lose it.
      }
    }

    await enqueueVoiceNote({
      contentType: result.contentType,
      durationMs: result.durationMs,
      purpose,
      bytes: result.blob,
      ...(captureId ? { captureId } : { queuedCaptureLocalId }),
    });
    setState('queued');
    onRecorded?.({ purpose, durationMs: result.durationMs, queued: true });
  }

  async function start() {
    setError(null);
    setState('requesting');
    try {
      const stream = await requestMicrophoneStream();
      streamRef.current = stream;
      setElapsedMs(0);
      handleRef.current = startVoiceRecording({
        stream,
        onElapsed: setElapsedMs,
        onAutoStop: (result) => void finish(result),
      });
      setState('recording');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not start the microphone.');
      setState('error');
    }
  }

  async function stop() {
    const handle = handleRef.current;
    if (!handle) return;
    const result = await handle.stop();
    await finish(result);
  }

  function handleClick() {
    if (state === 'recording') {
      void stop();
    } else {
      void start();
    }
  }

  const busy = state === 'requesting' || state === 'uploading';
  const statusText = state === 'recording' ? formatElapsed(elapsedMs) : state === 'error' ? error : STATUS_LINE[state];

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* 44px target: the mic sits beside frames and links an operator
          taps at arm's length, standing at the item. */}
      <Button
        type="button"
        size="icon"
        variant={state === 'recording' ? 'destructive' : 'outline'}
        disabled={!attached || busy}
        aria-label={state === 'recording' ? 'Stop recording' : LABEL[purpose]}
        aria-pressed={state === 'recording'}
        onClick={handleClick}
      >
        {busy ? (
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        ) : state === 'recording' ? (
          <Square className="size-4" aria-hidden="true" />
        ) : (
          <Mic className="size-4" aria-hidden="true" />
        )}
      </Button>
      {statusText && (
        <p className="text-xs text-muted" aria-live="polite">
          {statusText}
        </p>
      )}
    </div>
  );
}
